import React, { useState, useEffect } from "react";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Tabs } from "../UI";
import { Tab } from "../UI/Tabs";
import LoadingContainer from "../layouts/LoadingContainer";
import "react-quill/dist/quill.snow.css";
import { createSearchParams, Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import ExpandOpen from "../asserts/img/expand_open.svg";
import ExpandClose from "../asserts/img/expand_close.svg";
import { startOfMonth, endOfMonth, subMonths } from "date-fns";
import DeleteImg from "../asserts/img/delete_doc.svg";
import EditImg from "../asserts/img/edit_doc.svg";
import {
  Item,
  MobileAddButton,
  renderKeyValue,
  formatDateOnly,
} from "../components/Common";
import MobileHeader from "../layouts/MobileHeader";
import ErrorNotification from "../components/ErrorNotification";

const IndexValue = styled.label(({ theme }) => ({
  fontSize: 25,
  fontWeight: 800,
  color: theme.color.lightText,
}));

export const RowLink = styled(Link)(() => ({
  cursor: "pointer",

  "&:hover *": {
    fontWeight: "bolder",
    cursor: "pointer",
    userSelect: "none",
  },
}));

const ReportDetailMobileRow = ({
  report,
  index,
  keys,
  handelRowDeleteClick,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("report_details");
  const navigate = useNavigate();

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        flexDirection: "column",
        borderBottom: `1px solid #D9D9D9`,
        paddingBottom: 14,
      }}>
      <div
        style={{
          display: "flex",
          gap: 20,
          width: "100%",
          alignItems: "center",
          flex: 1,
          padding: 15,
          paddingBottom: 5,
        }}>
        <IndexValue>
          {(index + 1).toLocaleString("en-US", {
            minimumIntegerDigits: 2,
            useGrouping: false,
          })}
        </IndexValue>
        <img
          src={open ? ExpandOpen : ExpandClose}
          alt="expand"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginInlineStart: "auto",
          }}>
          <button
            onClick={(e) => {
              navigate({
                pathname: "/report_detail",
                search: createSearchParams({
                  itemId: report.DETAIL,
                }).toString(),
              });
            }}
            style={{
              border: 0,
              backgroundColor: "white",
              cursor: "pointer",
            }}>
            <img alt="EditImg" src={EditImg} style={{ marginInlineEnd: 5 }} />
          </button>
          <button
            onClick={(e) => handelRowDeleteClick(e, report)}
            style={{
              border: 0,
              backgroundColor: "white",
              cursor: "pointer",
            }}>
            <img
              alt="DeleteImg"
              src={DeleteImg}
              style={{ marginInlineEnd: 5 }}
            />
          </button>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          gap: 20,
          width: "100%",
          alignItems: "start",
          flex: 1,
          paddingInline: 20,
        }}>
        <Item
          label={t("CURDATE")}
          value={formatDateOnly(report.CURDATE, "dd/MM/yyyy")}
          width={"50%"}
        />
        <Item label={t("CUSTDES")} value={report.CUSTDES} width={"50%"} />
      </div>
      {open && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            padding: 20,
            paddingTop: 0,
            paddingBottom: 10,
            width: "100%",
          }}>
          {keys.map((key) => (
            <Item
              label={t(key.key)}
              value={renderKeyValue(key, report)}
              width={key.width}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ReportDetailsMobile = ({
  reportDetailsStore,
  reportDetails,
  handelRowDeleteClick,
  handleSearchChange,
  handleDateChange,
  inSearch,
  date,
  error,
}) => {
  const { t } = useTranslation("report_details");
  const [tab, setTab] = useState("1");
  const [showErrorNotification, setShowErrorNotification] = useState({
    show: false,
    text: "",
  });

  useEffect(() => {
    if (!!error) {
      setShowErrorNotification({
        show: true,
        text: error,
      });
    } else {
      setShowErrorNotification({
        show: false,
        text: "",
      });
    }
  }, [error]);

  const navigate = useNavigate();

  const keys = [
    { key: "ORDNAME", width: "50%", sort: true },
    { key: "PARTDES", width: "50%", sort: true },
    { key: "JOBDESC", width: "50%", sort: true },
    { key: "ZCOR_TASKDES", width: "50%", sort: true },
    { key: "ZCOR_DOCNO", width: "50%", sort: true },

    { key: "HOURS", width: "50%" },
    { key: "NO_CHARGE", width: "50%" },
  ];

  const linkTo = (report) => {
    reportDetailsStore.setSelectedReportDetail(report);

    return {
      pathname: "/report_detail",
      search: `?itemId=${report.DETAIL}`,
    };
  };

  return (
    <>
      <LoadingContainer>
        <MobileAddButton
          handelOpenNewClick={() => navigate("/report_detail")}
        />
        <MobileHeader
          handleDateChange={(date) => {
            setTab("3");
            handleDateChange(date);
          }}
          handleSearchChange={(search) => {
            setTab("3");
            handleSearchChange(search);
          }}
          title={t("report_details_title")}
          date={date}
          setDate={handleDateChange}>
          <Tabs value={tab} tabChange={setTab} width={"33vw"}>
            <Tab value="1" label={t("current_month")}>
              <div
                style={{
                  overflowY: "auto",
                  resize: "vertical",
                  height: `calc( 100vh - 100px)`,
                }}>
                {reportDetails
                  ?.filter((rep) => {
                    const date = new Date();
                    const reportDate = new Date(rep.CURDATE);

                    return (
                      reportDate.getTime() >= startOfMonth(date).getTime() &&
                      reportDate.getTime() <= endOfMonth(date).getTime()
                    );
                  })
                  .map((report, index) => (
                    <ReportDetailMobileRow
                      keys={keys}
                      key={index}
                      report={report}
                      index={index}
                      linkTo={linkTo}
                      handelRowDeleteClick={handelRowDeleteClick}
                    />
                  ))}
              </div>
            </Tab>
            <Tab value="2" label={t("last_month")}>
              <div
                style={{
                  overflowY: "auto",
                  resize: "vertical",
                  height: `calc( 100vh - 100px)`,
                }}>
                {reportDetails
                  ?.filter((rep) => {
                    const date = new Date();
                    const dateLastMonth = new Date(subMonths(date, 1));

                    const reportDate = new Date(rep.CURDATE);

                    return (
                      reportDate.getTime() >=
                        startOfMonth(dateLastMonth).getTime() &&
                      reportDate.getTime() <=
                        endOfMonth(dateLastMonth).getTime()
                    );
                  })
                  .map((report, index) => (
                    <ReportDetailMobileRow
                      keys={keys}
                      key={index}
                      report={report}
                      index={index}
                      linkTo={linkTo}
                      handelRowDeleteClick={handelRowDeleteClick}
                    />
                  ))}
              </div>
            </Tab>
            <Tab value="3" label={t("search_details")}>
              {inSearch ? (
                <div
                  style={{
                    overflowY: "auto",
                    resize: "vertical",
                    height: `calc( 100vh - 100px)`,
                  }}>
                  {reportDetails.map((report, index) => (
                    <ReportDetailMobileRow
                      keys={keys}
                      key={index}
                      report={report}
                      index={index}
                      linkTo={linkTo}
                      handelRowDeleteClick={handelRowDeleteClick}
                    />
                  ))}
                </div>
              ) : (
                []
              )}
            </Tab>
          </Tabs>
        </MobileHeader>
        {showErrorNotification.show && (
          <ErrorNotification
            text={showErrorNotification.text}
            onClose={() => setShowErrorNotification({ show: false })}
          />
        )}
      </LoadingContainer>
    </>
  );
};

export default inject(
  "userStore",
  "reportDetailsStore"
)(observer(ReportDetailsMobile));
